/**
 * LAS PARCELAS DEL CUADERNO Y SU Nº DE ORDEN (CQ2).
 *
 * Para el modelo v9 una «parcela» del 2.1 es un cultivo en un sitio: aquí,
 * NAVE × ESPECIE × VARIEDAD × CICLO. Dos sectores de la misma nave plantados el
 * mismo día con lo mismo son UNA parcela; el mismo sector con un segundo ciclo
 * en el año (tomate en primavera, pepino en otoño) son DOS.
 *
 *   - El nº de orden sale ordenando por finca, nave (orden natural), inicio del
 *     ciclo y cultivo: el mismo cuaderno da siempre los mismos números.
 *   - En el 2.1 va UNA fila por recinto SIGPAC confirmado de la nave,
 *     repitiendo el nº de orden (una nave que pisa dos recintos son dos filas con
 *     el mismo nº). Sin recinto confirmado, una fila sin SIGPAC: hueco.
 *   - «TODAS» en Id. parcelas cuando un tratamiento cubre todas las parcelas con
 *     cultivo ese día (y hay más de una: con una sola se escribe su nº).
 *
 * Un parte encuentra su parcela por su ciclo (`crop_cycle_id`); si no lo tiene,
 * por el ciclo del sector activo ese día; si tampoco, por el cultivo del sector.
 */
import { naturalCompare } from "@/lib/sorting";
import type { CicloEntrada, FincaEntrada, NaveEntrada, ParteEntrada, RecintoEntrada, SectorEntrada } from "./entrada";

export interface ParcelaPest {
  orden: number;
  clave: string;
  naveId: string;
  fincaId: string | null;
  especie: string | null;
  /** Código SIEX de la especie (del ciclo o del sector), si lo hay. NO entra en la clave (F4 cultivos SIEX). */
  especieCodigo?: number | null;
  variedad: string | null;
  /** Inicio y fin del ciclo; null si la parcela sale del cultivo del sector (sin ciclo). */
  inicio: string | null;
  fin: string | null;
  sectores: string[];
  /** Suma del área de sus sectores (m²); null si ninguno la tiene. */
  areaM2: number | null;
  /** De dónde salió el cultivo: el ciclo o la ficha del sector. */
  cultivoDesde: "ciclo" | "sector" | null;
}

const norm = (s: string | null | undefined) =>
  String(s ?? "").trim().toLocaleLowerCase("es").normalize("NFD").replace(/[̀-ͯ]/g, "");

/** ¿Solapa el ciclo [inicio, fin] con el periodo [desde, hasta]? */
export function solapa(inicio: string, fin: string | null | undefined, desde: string, hasta: string): boolean {
  return inicio <= hasta && (!fin || fin >= desde);
}

export interface PlanParcelas {
  parcelas: ParcelaPest[];
  /** La parcela de un parte, o null si no tiene nave. */
  parcelaDeParte(p: Pick<ParteEntrada, "id" | "fecha" | "naveId" | "blockId" | "cropCycleId">): ParcelaPest | null;
  /** «TODAS» o los nº de orden, para un conjunto de parcelas tratadas un día. */
  idParcelas(ordenes: number[], fecha: string): "TODAS" | number[];
}

export function planDeParcelas(args: {
  periodo: { desde: string; hasta: string };
  fincas: FincaEntrada[];
  naves: NaveEntrada[];
  sectores: SectorEntrada[];
  ciclos: CicloEntrada[];
  partes: ParteEntrada[];
}): PlanParcelas {
  const { periodo } = args;
  const nave = new Map(args.naves.map((n) => [n.id, n]));
  const finca = new Map(args.fincas.map((f) => [f.id, f]));
  const sector = new Map(args.sectores.map((s) => [s.id, s]));
  const ciclo = new Map(args.ciclos.map((c) => [c.id, c]));

  type Borrador = Omit<ParcelaPest, "orden" | "areaM2" | "sectores"> & { sectores: Set<string> };
  const porClave = new Map<string, Borrador>();

  const naveDeSector = (blockId: string | null | undefined) => (blockId ? sector.get(blockId)?.naveId ?? null : null);

  const alta = (
    naveId: string,
    c: { especie: string | null; especieCodigo?: number | null; variedad: string | null; inicio: string | null; fin: string | null; desde: ParcelaPest["cultivoDesde"] },
    blockId: string | null,
  ) => {
    // La clave NO lleva el código (F4 cultivos SIEX): el borrador guarda `orden_parcelas` por esta clave.
    const clave = [naveId, norm(c.especie), norm(c.variedad), c.inicio ?? ""].join("|");
    let b = porClave.get(clave);
    if (b && b.especieCodigo == null && c.especieCodigo != null) b.especieCodigo = c.especieCodigo;
    if (!b) {
      b = {
        clave,
        naveId,
        fincaId: nave.get(naveId)?.locationId ?? null,
        especie: c.especie?.trim() || null,
        ...(c.especieCodigo != null ? { especieCodigo: c.especieCodigo } : {}),
        variedad: c.variedad?.trim() || null,
        inicio: c.inicio,
        fin: c.fin,
        sectores: new Set(),
        cultivoDesde: c.desde,
      };
      porClave.set(clave, b);
    }
    if (blockId) b.sectores.add(blockId);
    return clave;
  };

  const claveDeCiclo = (c: CicloEntrada) => {
    const naveId = naveDeSector(c.blockId);
    if (!naveId || !nave.has(naveId)) return null;
    return alta(naveId, { especie: c.especie, especieCodigo: c.especieCodigo ?? null, variedad: c.variedad ?? null, inicio: c.inicio, fin: c.fin ?? null, desde: "ciclo" }, c.blockId);
  };

  // 1. Todos los ciclos del periodo en las naves del cuaderno, se tratara o no.
  for (const c of args.ciclos) {
    if (solapa(c.inicio, c.fin, periodo.desde, periodo.hasta)) claveDeCiclo(c);
  }

  // 2. Los partes encuentran (o crean) su parcela.
  const claveDeParte = new Map<string, string | null>();
  const cicloActivo = (blockId: string | null | undefined, fecha: string) =>
    blockId ? args.ciclos.find((c) => c.blockId === blockId && c.inicio <= fecha && (!c.fin || c.fin >= fecha)) ?? null : null;

  for (const p of args.partes) {
    const naveId = p.naveId ?? naveDeSector(p.blockId);
    if (!naveId || !nave.has(naveId)) {
      claveDeParte.set(p.id, null);
      continue;
    }
    const c = (p.cropCycleId ? ciclo.get(p.cropCycleId) : null) ?? cicloActivo(p.blockId, p.fecha);
    let clave: string | null = null;
    if (c && naveDeSector(c.blockId) === naveId) clave = claveDeCiclo(c);
    if (!clave) {
      const s = p.blockId ? sector.get(p.blockId) : null;
      clave = alta(
        naveId,
        { especie: s?.cultivo ?? null, especieCodigo: s?.cultivo ? s?.cultivoCodigo ?? null : null, variedad: s?.variedad ?? null, inicio: null, fin: null, desde: s?.cultivo ? "sector" : null },
        p.blockId,
      );
    }
    claveDeParte.set(p.id, clave);
  }

  // 3. Orden estable: finca, nave, inicio del ciclo, cultivo.
  const nombreFinca = (b: Borrador) => (b.fincaId ? finca.get(b.fincaId)?.nombre ?? "" : "");
  const ordenadas = [...porClave.values()].sort(
    (a, b) =>
      naturalCompare(nombreFinca(a), nombreFinca(b)) ||
      naturalCompare(nave.get(a.naveId)?.nombre ?? "", nave.get(b.naveId)?.nombre ?? "") ||
      (a.inicio ?? "").localeCompare(b.inicio ?? "") ||
      naturalCompare(a.especie ?? "", b.especie ?? "") ||
      naturalCompare(a.variedad ?? "", b.variedad ?? ""),
  );

  const parcelas: ParcelaPest[] = ordenadas.map((b, i) => {
    const sectores = [...b.sectores];
    const areas = sectores.map((id) => Number(sector.get(id)?.areaM2)).filter((n) => Number.isFinite(n) && n > 0);
    return { ...b, orden: i + 1, sectores, areaM2: areas.length ? areas.reduce((s, n) => s + n, 0) : null };
  });
  const porClaveFinal = new Map(parcelas.map((p) => [p.clave, p]));

  const activasEn = (fecha: string) =>
    parcelas.filter((p) => (p.inicio ? p.inicio <= fecha && (!p.fin || p.fin >= fecha) : true)).map((p) => p.orden);

  return {
    parcelas,
    parcelaDeParte: (p) => {
      if (!claveDeParte.has(p.id)) return null;
      const k = claveDeParte.get(p.id);
      return k ? porClaveFinal.get(k) ?? null : null;
    },
    idParcelas: (ordenes, fecha) => {
      const unicos = [...new Set(ordenes)].sort((a, b) => a - b);
      const activas = activasEn(fecha);
      if (activas.length > 1 && activas.every((o) => unicos.includes(o))) return "TODAS";
      return unicos;
    },
  };
}

/** Los recintos CONFIRMADOS de cada nave (el cuaderno solo imprime esos). */
/**
 * Cómo se reparte la superficie cultivada de una nave entre sus recintos
 * confirmados (25-10-2026): una fracción por recinto que suma 1, o null si no se
 * sabe. En la hoja 2.1 cada recinto lleva la superficie cultivada que cae en él;
 * antes se repetía la nave entera en cada uno (Nave 1 de SAT Inver con tres
 * recintos = 4,71 ha en vez de 1,57).
 *   · un recinto → todo en él;
 *   · varios, con `parteNave` medida en todos → en proporción a lo medido;
 *   · si no, con `muestras` en todos → en proporción a los puntos del WMS;
 *   · si tampoco → null: no se inventa, la superficie se deja vacía y se pide.
 */
export function repartoEntreRecintos(recs: Pick<RecintoEntrada, "parteNave" | "muestras">[]): number[] | null {
  if (recs.length <= 1) return recs.map(() => 1);
  const proporcion = (xs: (number | null | undefined)[]) => {
    if (!xs.every((x) => x != null && Number.isFinite(x) && x > 0)) return null;
    const total = (xs as number[]).reduce((a, x) => a + x, 0);
    return total > 0 ? (xs as number[]).map((x) => x / total) : null;
  };
  return proporcion(recs.map((r) => r.parteNave)) ?? proporcion(recs.map((r) => r.muestras));
}

export function recintosConfirmados(recintos: RecintoEntrada[] | undefined): Map<string, RecintoEntrada[]> {
  const m = new Map<string, RecintoEntrada[]>();
  for (const r of recintos ?? []) {
    if (!r.confirmadoEn) continue;
    const l = m.get(r.greenhouseId) ?? [];
    l.push(r);
    m.set(r.greenhouseId, l);
  }
  return m;
}
